import { For, Show } from "solid-js"
import { Button } from "@openanalyst-ai/ui/button"
import { Card } from "@openanalyst-ai/ui/card"

type WidgetActionDeclaration = {
  id: string
  label: string
  variant?: "primary" | "secondary" | "ghost"
  disabled?: boolean
  disabledReason?: string
}

type DetailWidgetData = {
  sections?: Array<{
    title?: string
    items: Array<{
      label: string
      value: string
      hint?: string
    }>
  }>
}

type WidgetState = {
  actionState?: Record<string, { pending: boolean; error?: string; result?: unknown }>
}

const renderValue = (value: unknown) => {
  if (typeof value === "string") return value
  return JSON.stringify(value, null, 2)
}

const machineValue = (value: string) => value.length > 24 && !/\s/.test(value)

export function DetailWidget(props: {
  data?: DetailWidgetData
  actions?: WidgetActionDeclaration[]
  state?: WidgetState
  onAction: (actionId: string) => void
}) {
  return (
    <div class="flex flex-col gap-4">
      <Show when={props.actions?.length}>
        <div class="flex flex-wrap gap-2">
          <For each={props.actions}>
            {(action) => (
              <Button
                size="small"
                variant={action.variant ?? "secondary"}
                disabled={Boolean(action.disabled || props.state?.actionState?.[action.id]?.pending)}
                title={action.disabled ? action.disabledReason : undefined}
                onClick={() => props.onAction(action.id)}
              >
                {props.state?.actionState?.[action.id]?.pending ? `${action.label}...` : action.label}
              </Button>
            )}
          </For>
        </div>
      </Show>

      <div class="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <For each={props.data?.sections ?? []}>
          {(section) => (
            <Card
              data-dashboard-detail-section=""
              class="rounded-lg border border-border-weak-base/70 bg-background-base/80 p-4 flex flex-col gap-3"
            >
              <Show when={section.title}>
                <h3 class="text-sm font-medium text-text-dimmed">{section.title}</h3>
              </Show>
              <div class="flex flex-col gap-3">
                <For each={section.items}>
                  {(item) => (
                    <div class="flex flex-col gap-1">
                      <span class="text-xs uppercase tracking-wide text-text-dimmed">{item.label}</span>
                      <span
                        title={item.value}
                        classList={{
                          "text-text-strong": true,
                          "break-words text-sm font-medium": !machineValue(item.value),
                          "break-all font-mono text-xs font-medium leading-relaxed": machineValue(item.value),
                        }}
                      >
                        {item.value}
                      </span>
                      <Show when={item.hint}>
                        <span class="text-xs text-text-dimmed">{item.hint}</span>
                      </Show>
                    </div>
                  )}
                </For>
              </div>
            </Card>
          )}
        </For>
      </div>

      <div class="flex flex-col gap-2">
        <For each={props.actions ?? []}>
          {(action) => (
            <Show when={props.state?.actionState?.[action.id]?.error || props.state?.actionState?.[action.id]?.result}>
              <Card
                variant={props.state?.actionState?.[action.id]?.error ? "error" : "info"}
                class="rounded-lg p-3 flex flex-col gap-2"
              >
                <span class="text-xs uppercase tracking-wide text-text-dimmed">{action.label}</span>
                <Show when={props.state?.actionState?.[action.id]?.error}>
                  <span class="text-sm text-text-danger">{props.state?.actionState?.[action.id]?.error}</span>
                </Show>
                <Show when={props.state?.actionState?.[action.id]?.result}>
                  <pre class="text-xs text-text-base whitespace-pre-wrap break-words">
                    {renderValue(props.state?.actionState?.[action.id]?.result)}
                  </pre>
                </Show>
              </Card>
            </Show>
          )}
        </For>
      </div>
    </div>
  )
}
